import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_PLUGINS, loadPlugins } from './plugins';
import './styles/rich-text-editor.css';
import type { RichTextEditorProps } from './types';
import { BrowserDOMPurify } from './utils/dompurify';

import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/froala_style.min.css';

const FroalaEditorComponent = React.lazy(() => import('react-froala-wysiwyg'));

const FULL_TOOLBAR_BUTTONS = {
  moreText: {
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikeThrough',
      'subscript',
      'superscript',
      'fontSize',
      'textColor',
      'backgroundColor',
      'inlineClass',
      'clearFormatting',
    ],
    buttonsVisible: 3,
    align: 'left',
  },
  moreParagraph: {
    buttons: [
      'alignLeft',
      'alignCenter',
      'alignRight',
      'alignJustify',
      'formatOL',
      'formatUL',
      'paragraphFormat',
      'lineHeight',
      'outdent',
      'indent',
      'quote',
    ],
    buttonsVisible: 3,
    align: 'left',
  },
  moreRich: {
    buttons: [
      'insertLink',
      'insertImage',
      'insertTable',
      'emoticons',
      'specialCharacters',
      'insertHR',
    ],
    buttonsVisible: 0,
    align: 'left',
  },
  moreMisc: {
    buttons: ['undo', 'redo', 'selectAll', 'help'],
    buttonsVisible: 2,
    align: 'right',
  },
  showMoreButtons: true,
} as const;

const COMPACT_TOOLBAR_BUTTONS = {
  moreText: {
    buttons: ['bold', 'italic', 'underline', 'strikeThrough', 'clearFormatting'],
    buttonsVisible: 2,
    align: 'left',
  },
  moreParagraph: {
    buttons: ['formatOL', 'formatUL', 'quote'],
    buttonsVisible: 2,
    align: 'left',
  },
  moreRich: {
    buttons: ['insertLink'],
    buttonsVisible: 1,
    align: 'left',
  },
  showMoreButtons: false,
} as const;

const createFroalaConfig = (
  placeholder?: string,
  autoFocus?: boolean,
  fillHeight?: boolean,
  compactToolbar?: boolean,
) => ({
  attribution: false,
  autofocus: !!autoFocus,
  ...(fillHeight ? {} : { heightMin: 100, heightMax: 450 }),
  placeholderText: placeholder || '',
  zIndex: 3700,
  toolbarButtons: compactToolbar ? COMPACT_TOOLBAR_BUTTONS : FULL_TOOLBAR_BUTTONS,
  linkStyles: {
    'fr-strong': 'Thick',
  },
  listAdvancedTypes: true,
  linkAlwaysBlank: true,
  imageUpload: false,
  imageEditButtons: [
    'imageReplace',
    'imageAlign',
    'imageRemove',
    '|',
    'imageLink',
    'linkOpen',
    'linkEdit',
    'linkRemove',
    '-',
    'imageDisplay',
    'imageAlt',
    'imageSize',
  ],
  language: 'en_ca',
  wordPasteModal: false,
});

export const RichTextEditor = React.memo<RichTextEditorProps>(
  ({
    id,
    defaultValue,
    onSave,
    onCancel,
    onBlur,
    onChange,
    plugins = DEFAULT_PLUGINS,
    placeholder,
    autoFocus = false,
    readOnly = false,
    className,
    fillHeight = false,
    compactToolbar = false,
  }) => {
    const { t } = useTranslation('translation');
    const [pluginsLoaded, setPluginsLoaded] = useState(false);
    const currentValueRef = useRef(defaultValue);

    useEffect(() => {
      let isMounted = true;

      const loadEditorPlugins = async () => {
        try {
          await loadPlugins(plugins);
          if (isMounted) {
            setPluginsLoaded(true);
          }
        } catch (error) {
          //biome-ignore lint/suspicious/noConsole: need to log this error
          console.error('Failed to load Froala plugins:', error);
          if (isMounted) {
            setPluginsLoaded(true);
          }
        }
      };

      loadEditorPlugins();

      return () => {
        isMounted = false;
      };
    }, [plugins]);

    const handleSave = useCallback(() => {
      onSave?.(BrowserDOMPurify.sanitize(currentValueRef.current));
    }, [onSave]);

    const handleCancel = useCallback(() => {
      onCancel?.();
    }, [onCancel]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: onBlur is omitted intentionally — Froala reads config once on mount and never re-applies it, so updating the dep array has no effect. Callers must pass a stable onBlur reference.
    const froalaConfig = useMemo(
      () => ({
        ...createFroalaConfig(placeholder, autoFocus, fillHeight, compactToolbar),
        events: {
          // biome-ignore lint/suspicious/noExplicitAny: Froala editor instance typed as any
          initialized(this: any) {
            if (readOnly) {
              this.edit.off();
            }
          },
          'paste.beforeCleanup': (html: string | Node) => BrowserDOMPurify.sanitize(html),
          blur: () => {
            onBlur?.(BrowserDOMPurify.sanitize(currentValueRef.current));
          },
        },
      }),
      [placeholder, autoFocus, readOnly, fillHeight, compactToolbar],
    );

    if (!pluginsLoaded) {
      return (
        <div className={cn('flex flex-col gap-2', fillHeight && 'min-h-0 flex-1', className)}>
          <div
            className={cn('rich-text-skeleton', fillHeight && 'rich-text-skeleton--fill-height')}
          >
            {t('common.loading')}
          </div>
        </div>
      );
    }

    return (
      <div id={id} className={cn('flex flex-col gap-2', fillHeight && 'min-h-0 flex-1', className)}>
        <div className={cn('rich-text-editor', fillHeight && 'rich-text-editor--fill-height')}>
          <Suspense
            fallback={
              <div
                className={cn(
                  'rich-text-skeleton',
                  fillHeight && 'rich-text-skeleton--fill-height',
                )}
              />
            }
          >
            <FroalaEditorComponent
              tag="textarea"
              model={defaultValue}
              config={froalaConfig}
              onModelChange={(newValue: string) => {
                currentValueRef.current = newValue;
                onChange?.(newValue);
              }}
              data-testid="rich-text-editor"
            />
          </Suspense>
        </div>

        {(onSave || onCancel) && (
          <div className="flex gap-2">
            {onSave && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                data-testid="rich-text-editor-save"
              >
                {t('ideaManagement.general.save')}
              </Button>
            )}
            {onCancel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                data-testid="rich-text-editor-cancel"
              >
                {t('common.cancel')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  },
);
